import { useState } from "react";
import { Plus, Pencil, ClipboardList, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { PageHeader, Badge, SkeletonGrid, ErrorState, EmptyState } from "../../components/common";
import { Field, Input, Select, STATUS_OPTS, Toolbar, Table, Row, Td, IconBtn, FormDialog, ConfirmDelete } from "../../components/admin/kit";

const newQ = () => ({ _k: Math.random().toString(36).slice(2), text: "", options: ["", "", "", ""], correct_index: 0 });

const removeOption = (q, oi) => {
  let correct = q.correct_index;
  if (correct === oi) correct = 0;
  else if (correct > oi) correct -= 1;
  return { options: q.options.filter((_, k) => k !== oi), correct_index: correct };
};

function QuestionsEditor({ qs, onChange }) {
  const upd = (i, patch) => onChange(qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold">Sorular <span className="font-normal text-slate-400">(çoktan seçmeli, tek doğru)</span></p>
        <button type="button" onClick={() => onChange([...qs, newQ()])} className="btn-ghost inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold" data-testid="add-question-btn"><Plus className="h-3.5 w-3.5" /> Soru Ekle</button>
      </div>
      {qs.map((q, i) => (
        <div key={q.id || q._k} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4" data-testid={`question-editor-${i}`}>
          <div className="flex gap-2">
            <span className="pt-3 font-mono text-xs text-slate-400">S{i + 1}</span>
            <Input value={q.text} onChange={(e) => upd(i, { text: e.target.value })} placeholder="Soru metni" required data-testid={`question-text-input-${i}`} />
            <IconBtn type="button" icon={X} label="Soruyu kaldır" danger onClick={() => onChange(qs.filter((_, j) => j !== i))} />
          </div>
          <div className="mt-3 space-y-2 sm:pl-7">
            {q.options.map((o, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <input type="radio" name={`correct-${i}`} checked={q.correct_index === oi} onChange={() => upd(i, { correct_index: oi })} className="h-4 w-4 accent-emerald-600" title="Doğru cevap" data-testid={`question-${i}-correct-${oi}`} />
                <Input value={o} onChange={(e) => upd(i, { options: q.options.map((x, k) => (k === oi ? e.target.value : x)) })} placeholder={`Seçenek ${String.fromCharCode(65 + oi)}`} required data-testid={`question-${i}-option-${oi}`} />
                {q.options.length > 2 && <IconBtn type="button" icon={X} label="Seçeneği kaldır" onClick={() => upd(i, removeOption(q, oi))} />}
              </div>
            ))}
            {q.options.length < 6 && <button type="button" onClick={() => upd(i, { options: [...q.options, ""] })} className="text-xs font-semibold text-[var(--brand)] hover:underline">+ Seçenek ekle</button>}
            <p className="text-[11px] text-slate-400">Doğru cevabı soldaki işaretle belirleyin.</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminExams() {
  const { data, loading, error, reload } = useApi("/admin/exams");
  const courses = useApi("/admin/courses");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.course_id) return toast.error("Lütfen bir eğitim seçin.");
    setSaving(true);
    try {
      const body = { title: form.title, course_id: form.course_id, pass_score: Number(form.pass_score), max_attempts: Number(form.max_attempts), status: form.status, questions: form.questions };
      if (form.id) await api.put(`/admin/exams/${form.id}`, body);
      else await api.post("/admin/exams", body);
      toast.success(form.id ? "Sınav güncellendi." : "Sınav oluşturuldu.");
      setForm(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const del = (e) => api.delete(`/admin/exams/${e.id}`).then(() => { toast.success("Sınav silindi."); reload(); }).catch((x) => toast.error(errMsg(x)));
  const list = (data || []).filter((e) => `${e.title} ${e.course_title}`.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR")));
  return (
    <div>
      <PageHeader eyebrow="Yönetim" title="Sınavlar" desc="Eğitimlere bağlı temel sınavlar." />
      <Toolbar q={q} setQ={setQ} placeholder="Sınav ara..." action={
        <button onClick={() => setForm({ title: "", course_id: courses.data?.[0]?.id || "", pass_score: 70, max_attempts: 3, status: "active", questions: [newQ()] })} className="btn-primary inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="add-exam-btn"><Plus className="h-4 w-4" /> Yeni Sınav</button>
      } />
      {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <SkeletonGrid count={1} className="h-72" cols="" /> : list.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Henüz sınav oluşturulmadı." desc="Bir eğitime bağlı sınav oluşturun." />
      ) : (
        <Table head={["Sınav", "Eğitim", "Soru", "Geçme", "Deneme", "Durum", ""]} testid="exams-table">
          {list.map((e) => (
            <Row key={e.id} data-testid={`exam-row-${e.id}`}>
              <Td className="font-semibold text-[var(--ink)]">{e.title}</Td>
              <Td className="text-slate-600">{e.course_title}</Td>
              <Td>{e.questions.length}</Td>
              <Td>{e.pass_score}</Td>
              <Td>{e.max_attempts}</Td>
              <Td><Badge status={e.status} /></Td>
              <Td>
                <div className="flex justify-end gap-1">
                  <IconBtn icon={Pencil} label="Düzenle" onClick={() => setForm(e)} data-testid={`edit-exam-${e.id}`} />
                  <ConfirmDelete onConfirm={() => del(e)} testid={`delete-exam-${e.id}`} />
                </div>
              </Td>
            </Row>
          ))}
        </Table>
      )}
      <FormDialog wide open={!!form} onClose={() => setForm(null)} title={form?.id ? "Sınavı Düzenle" : "Yeni Sınav"} onSubmit={save} saving={saving} testid="exam-form-dialog">
        {form && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Sınav Adı"><Input value={form.title} onChange={set("title")} required data-testid="exam-title-input" /></Field>
              <Field label="Eğitim"><Select value={form.course_id} onChange={set("course_id")} options={(courses.data || []).map((c) => [c.id, c.title])} data-testid="exam-course-select" /></Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Geçme Puanı (0-100)"><Input type="number" min={0} max={100} value={form.pass_score} onChange={set("pass_score")} data-testid="exam-pass-score-input" /></Field>
              <Field label="Deneme Hakkı"><Input type="number" min={1} max={20} value={form.max_attempts} onChange={set("max_attempts")} data-testid="exam-attempts-input" /></Field>
              <Field label="Durum"><Select value={form.status} onChange={set("status")} options={STATUS_OPTS} data-testid="exam-status-select" /></Field>
            </div>
            <QuestionsEditor qs={form.questions} onChange={(questions) => setForm({ ...form, questions })} />
          </>
        )}
      </FormDialog>
    </div>
  );
}
